'use client'

import { useId, useState } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgCircleInfo from '@/icons/regular/CircleInfo'
import type { FilterOption } from '@/lib/directory/build-filter-options'
import { tw } from '@/lib/utils'

interface ChoiceCheckProps {
  id: string
  option: FilterOption
  selected: boolean
  onToggle: (value: string) => void
}

// A checkbox row for the multi-pick facets (style, brand, features,
// accessories): native checkbox, the label with its family count right after
// the last word, and — for options with a description — an info toggle that
// opens the explanation in flow beneath the row (no floating panel to clip
// inside the scrolling rail or the sheet).
export const ChoiceCheck = ({ id, option, selected, onToggle }: ChoiceCheckProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const descriptionId = useId()
  const label = option.label ?? option.value

  return (
    <div className="py-1">
      <div className="flex min-h-[32px] items-start gap-3">
        <input
          id={id}
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(option.value)}
          className="accent-lapis mt-[7px] h-[18px] w-[18px] shrink-0 cursor-pointer"
        />
        <label htmlFor={id} className="text-navy min-w-0 flex-1 cursor-pointer py-1.5 text-[15px] leading-snug">
          {label} <span className="text-navy/50 text-[12px] whitespace-nowrap tabular-nums">{option.count}</span>
        </label>
        {option.description ? (
          <button
            type="button"
            data-tracking="ignore"
            aria-expanded={open}
            aria-controls={descriptionId}
            aria-label={t('directory.filter.whatIsThis', 'What is {{label}}?', { label })}
            onClick={() => setOpen((v) => !v)}
            className={tw(
              'hover:text-navy focus-visible:outline-lapis -mr-1.5 flex h-8 w-8 shrink-0 cursor-help items-center justify-center rounded-full focus-visible:outline-2',
              open ? 'text-navy' : 'text-navy/45'
            )}
          >
            <SvgCircleInfo className="h-4 w-4 fill-current" aria-hidden />
          </button>
        ) : null}
      </div>
      {option.description ? (
        <p
          id={descriptionId}
          hidden={!open}
          className="text-navy/70 mb-2 ml-[30px] rounded-lg bg-white px-3 py-2 text-[13px] leading-snug"
        >
          {option.description}
        </p>
      ) : null}
    </div>
  )
}
